import { useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';
import { getHexTileMapKey } from '@/game/hexmap';
import { atHome, dpsText, durationText, enemyTip } from '@/game/view/focusQueue';
import { knownTerritories } from '@/game/view/lastSeen';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { useSimFrame } from './app/simFrame';
import { type UiStore, useUi } from './ui/store';

const OFFSET = 18;
const WIDTH = 208;

/**
 * Tooltip by the cursor on a rival unit in sight: its hp, what it does
 * (attack dps and range, or heal rate and range), and what the selected
 * units are expected to deal to it per type, with the time to kill
 * (`enemyTip`). Only for units on tiles the player can see now (fog), so it
 * never describes what is out of sight.
 */
export function EnemyTip({ ui }: { ui: UiStore }) {
  const frame = useSimFrame();
  const { view: state, rules, playerId, fog, playerOrder } = frame;
  const hoveredKey = useUi(ui, (s) => s.hoveredKey);
  const selected = useUi(ui, (s) => s.selectedUnitIds);
  const ref = useRef<HTMLDivElement>(null);
  // Follows the cursor without re-rendering; the last position is kept so a
  // tooltip that mounts under a still cursor is placed at once.
  const pointer = useRef({ x: -9999, y: -9999 });
  const place = () => {
    const el = ref.current;
    if (!el) return;
    const x = Math.min(pointer.current.x + OFFSET, window.innerWidth - WIDTH - 8);
    const y = Math.min(pointer.current.y + OFFSET, window.innerHeight - el.offsetHeight - 8);
    el.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`;
  };
  const placeEvent = useEffectEvent(place);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      pointer.current = { x: e.clientX, y: e.clientY };
      placeEvent();
    };
    window.addEventListener('pointermove', move);
    return () => window.removeEventListener('pointermove', move);
  }, []);
  // Every render (the tip's content changes its height).
  useLayoutEffect(place);
  if (!hoveredKey || !fog.visible.has(hoveredKey)) return null;
  const unit = Object.values(state.units).find(
    (u) => u.owner !== playerId && !u.inside && getHexTileMapKey(u.pos) === hoveredKey
  );
  if (!unit) return null;
  // Home defence only matters for an estimate, which needs selected fighters.
  const home =
    selected.length > 0 &&
    atHome(
      knownTerritories(state, frame.knownBuildings(), playerId, rules, (key) => fog.sight(key)),
      unit
    );
  const tip = enemyTip(rules, state, unit, selected, playerId, home);
  const color = PLAYER_COLORS[colorIndexFor(tip.owner, playerOrder)] ?? '#fff';
  const focused = selected.some((id) => {
    const order = state.units[id]?.order;
    return order?.kind === 'attack' && order.targetId === unit.id;
  });
  return (
    <div
      ref={ref}
      className="panel pointer-events-none fixed left-0 top-0 z-30 flex flex-col gap-1 bg-surface-strong px-2.5 py-2 text-xs"
      style={{ width: WIDTH, transform: 'translate(-9999px, -9999px)' }}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-fg-muted">{tip.glyph}</span>
        <span className="min-w-0 flex-1 truncate font-medium">
          {tip.name}{' '}
          <span className="font-normal text-fg-subtle">
            ·{' '}
            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: color }} />{' '}
            {frame.playerName(tip.owner)}
          </span>
        </span>
        {focused && <span className="chip text-danger">focus</span>}
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-fill">
        <div
          className={`h-full rounded-full ${tip.hp < tip.maxHp / 2 ? 'bg-warn' : 'bg-ok'}`}
          style={{ width: `${Math.min(1, tip.hp / tip.maxHp) * 100}%` }}
        />
      </div>
      <Line label="hp" value={`${Math.ceil(tip.hp)}/${Math.ceil(tip.maxHp)}`} />
      {tip.heals && (
        <Line label="heals" value={`${tip.heals.rate} hp/s · range ${tip.heals.range}`} />
      )}
      {tip.attack ? (
        <Line label="attack" value={`${dpsText(tip.attack.dps)} · range ${tip.attack.range}`} />
      ) : (
        <Line label="attack" value="none" muted />
      )}
      {selected.length > 0 && (
        <>
          <div className="border-t border-line" />
          <span className="text-[10px] uppercase tracking-wide text-fg-subtle">
            Your expected damage
          </span>
          {tip.report.groups.length === 0 ? (
            <span className="text-[11px] text-fg-subtle">Your selection can't hit it</span>
          ) : (
            tip.report.groups.map((g) => (
              <div key={g.type} className="flex items-baseline gap-1.5 text-[11px]">
                <span className="text-fg-muted">{g.glyph}</span>
                <span className="flex-1 truncate text-fg-muted">
                  ×{g.count} · {dpsText(g.each)} each
                  {g.chance !== null && (
                    <span className="text-fg-subtle"> ({Math.round(g.chance * 100)}%)</span>
                  )}
                </span>
                <span className="font-medium tabular-nums text-ok">{dpsText(g.total)}</span>
              </div>
            ))
          )}
          {tip.ttk !== null && (
            <span className="text-[11px]">
              Dead in about <b className="font-medium">{durationText(tip.ttk)}</b>
              {tip.report.groups.length > 1 ? ' with all' : ''}
            </span>
          )}
        </>
      )}
    </div>
  );
}

function Line({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-[11px]">
      <span className="text-fg-muted">{label}</span>
      <span className={`tabular-nums ${muted ? 'text-fg-subtle' : ''}`}>{value}</span>
    </div>
  );
}
